import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import {
  Wrench, AlertTriangle, RefreshCw, CalendarClock, Circle,
} from 'lucide-react';
import type { SiteEvent } from '../../../redux/api/eventsApi';
import { useTheme } from '../../../contexts';
import { deriveEventCategory, ALL_EVENT_CATEGORIES, EVENT_CATEGORY_CONFIG, type EventCategory } from '../../../constants/eventCategories';
import { SEVERITY_HEX_COLORS } from '../../../constants/eventStyles';

const CATEGORY_GRADIENTS: Record<EventCategory, { from: string; to: string }> = {
  preventative_maintenance: { from: '#3B82F6', to: '#93C5FD' },
  corrective_maintenance: { from: '#EF4444', to: '#FCA5A5' },
  asset_update: { from: '#8B5CF6', to: '#C4B5FD' },
  upcoming: { from: '#F59E0B', to: '#FCD34D' },
  other: { from: '#6B7280', to: '#D1D5DB' },
};

const CATEGORY_ICONS: Record<EventCategory, React.ComponentType<{ className?: string }>> = {
  preventative_maintenance: Wrench,
  corrective_maintenance: AlertTriangle,
  asset_update: RefreshCw,
  upcoming: CalendarClock,
  other: Circle,
};

interface SiteTimelineModuleProps {
  events: SiteEvent[];
  onEventClick?: (eventId: string) => void;
  onOpenTimeline?: () => void;
}

const ROW_HEIGHT = 44;
const LABEL_WIDTH = 12; // minimal left padding, no label column for single-site
const PADDING_RIGHT = 12;

const SiteTimelineModule: React.FC<SiteTimelineModuleProps> = ({
  events,
  onEventClick,
  onOpenTimeline,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [categoryFilters, setCategoryFilters] = useState<Set<EventCategory>>(new Set());

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  const categorizedEvents = useMemo(() => {
    return events.map((e) => ({
      ...e,
      category: (e.event_category || deriveEventCategory(e.event_type, e.occurred_at)) as EventCategory,
    }));
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (categoryFilters.size === 0) return categorizedEvents;
    return categorizedEvents.filter((e) => categoryFilters.has(e.category));
  }, [categorizedEvents, categoryFilters]);

  // Group by component type for multi-row display
  const componentRows = useMemo(() => {
    const typeMap = new Map<string, typeof filteredEvents>();
    const siteLevel: typeof filteredEvents = [];

    filteredEvents.forEach((event) => {
      // Use event_type as a rough grouping since we don't have component data here
      const group = event.event_type;
      if (!typeMap.has(group)) typeMap.set(group, []);
      typeMap.get(group)!.push(event);
    });

    // Sort groups by event count descending, take top rows
    const groups = Array.from(typeMap.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([type, evts]) => ({
        label: type.charAt(0).toUpperCase() + type.slice(1),
        events: evts,
      }));

    return groups;
  }, [filteredEvents]);

  const dateRange: [Date, Date] = useMemo(() => {
    const allDates = filteredEvents.map((e) => new Date(e.occurred_at).getTime());
    if (allDates.length === 0) {
      const now = Date.now();
      return [new Date(now - 365 * 86400000), new Date(now)];
    }
    const min = Math.min(...allDates);
    const max = Math.max(...allDates);
    const range = max - min || 86400000;
    return [new Date(min - range * 0.05), new Date(max + range * 0.05)];
  }, [filteredEvents]);

  const labelColWidth = 90;
  const timeScale = useMemo(
    () => d3.scaleTime().domain(dateRange).range([labelColWidth, width - PADDING_RIGHT]),
    [dateRange, width]
  );

  const axisTicks = useMemo(() => {
    const ticks = timeScale.ticks(6);
    return ticks.map((t) => ({
      x: timeScale(t),
      label: d3.timeFormat('%b %Y')(t),
    }));
  }, [timeScale]);

  const toggleCategory = useCallback((cat: EventCategory) => {
    setCategoryFilters((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const svgHeight = componentRows.length * ROW_HEIGHT + 24;

  if (events.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm py-8" style={{ color: 'var(--color-text-muted)' }}>
        No events to display
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full min-w-0">
      {/* Category filter chips */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {ALL_EVENT_CATEGORIES.map((cat) => {
          const Icon = CATEGORY_ICONS[cat];
          const isActive = categoryFilters.size === 0 || categoryFilters.has(cat);
          const grad = CATEGORY_GRADIENTS[cat];
          return (
            <button
              key={cat}
              onClick={() => toggleCategory(cat)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                isActive ? '' : 'opacity-30'
              }`}
              style={{
                backgroundColor: isActive ? `${grad.from}15` : 'transparent',
                color: grad.from,
              }}
            >
              <Icon className="w-3 h-3" />
              {EVENT_CATEGORY_CONFIG[cat].label}
            </button>
          );
        })}
      </div>

      <svg width={width} height={svgHeight} className="block">
        {/* Label column separator */}
        <line
          x1={labelColWidth} y1={0}
          x2={labelColWidth} y2={svgHeight - 24}
          stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}
        />

        {/* Axis tick lines */}
        {axisTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={tick.x} y1={0}
              x2={tick.x} y2={svgHeight - 24}
              stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}
            />
            <text
              x={tick.x} y={svgHeight - 6}
              textAnchor="middle"
              fill={isDark ? '#6B7280' : '#9CA3AF'}
              fontSize={9}
            >
              {tick.label}
            </text>
          </g>
        ))}

        {/* Event type rows */}
        {componentRows.map((row, rowIdx) => {
          const y = rowIdx * ROW_HEIGHT;
          return (
            <g key={row.label} transform={`translate(0, ${y})`}>
              {/* Row bg */}
              <rect
                x={0} y={0}
                width={width} height={ROW_HEIGHT}
                fill={rowIdx % 2 === 0
                  ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)')
                  : 'transparent'
                }
              />

              {/* Row label */}
              <text
                x={8} y={ROW_HEIGHT / 2}
                dominantBaseline="middle"
                fill={isDark ? '#D1D5DB' : '#4B5563'}
                fontSize={11}
                fontWeight={600}
              >
                {row.label}
              </text>

              {/* Event count */}
              <text
                x={labelColWidth - 8} y={ROW_HEIGHT / 2}
                dominantBaseline="middle"
                textAnchor="end"
                fill={isDark ? '#6B7280' : '#9CA3AF'}
                fontSize={9}
              >
                {row.events.length}
              </text>

              {/* Event dots */}
              {row.events.map((event) => {
                const x = timeScale(new Date(event.occurred_at));
                if (x < labelColWidth || x > width - PADDING_RIGHT) return null;
                const jitter = ((event.id.charCodeAt(0) % 5) - 2) * 3;
                const grad = CATEGORY_GRADIENTS[event.category];
                const sevColor = SEVERITY_HEX_COLORS[event.severity as keyof typeof SEVERITY_HEX_COLORS] || grad.from;
                return (
                  <g key={event.id}>
                    {/* Severity ring */}
                    <circle
                      cx={x}
                      cy={ROW_HEIGHT / 2 + jitter}
                      r={5.5}
                      fill="none"
                      stroke={sevColor}
                      strokeWidth={1.5}
                      opacity={0.4}
                    />
                    {/* Category dot */}
                    <circle
                      cx={x}
                      cy={ROW_HEIGHT / 2 + jitter}
                      r={4}
                      fill={grad.from}
                      opacity={0.85}
                      className="cursor-pointer hover:opacity-100 transition-opacity"
                      onClick={() => onEventClick?.(event.id)}
                    >
                      <title>{`${event.title} (${event.severity} · ${event.event_type})`}</title>
                    </circle>
                  </g>
                );
              })}

              {/* Row separator */}
              <line
                x1={0} y1={ROW_HEIGHT}
                x2={width} y2={ROW_HEIGHT}
                stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default SiteTimelineModule;
